import PropTypes from 'prop-types';
import { createContext, useContext, useEffect } from 'react';

import { DndContext, useSensor, useSensors, MouseSensor } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash } from 'lucide-react';
import { Button, ButtonIcon, DefaultTable, FormSection } from 'twyne-new.ui';

import Input from '@Components/form/Input';
import AutocompleteInput from '@Components/formsy/AutocompleteInput';

const DragHandleContext = createContext({})

function SortableRow({ children, row, className }) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: row.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  }
  return <DragHandleContext.Provider value={{ attributes, listeners }}>
    <tr ref={setNodeRef} style={style} className={className}>
      {children}
    </tr>
  </DragHandleContext.Provider>
}

function DragHandleFormatter() {
  const { attributes, listeners } = useContext(DragHandleContext)
  return <div {...attributes} {...listeners} className='cursor-grab flex justify-center'>
    <GripVertical size={16} className='hover:twy-text-base-accent' />
  </div>
}

const propTypes = {
  mergetags: PropTypes.array,
  fields: PropTypes.array,
  onChange: PropTypes.func,
}

export default function Items({ mergetags, fields, onChange }) {
  const sensors = useSensors(useSensor(MouseSensor, {}))

  useEffect(() => {
    if (fields.length === 0) {
      onChange([{ id: Date.now().toString(), key: '', value: '' }])
    }
  }, [fields, onChange])

  const handleAdd = () => {
    fields.push({ id: Date.now().toString(), key: '', value: '' })
    onChange([...fields])
  }

  const handleRemove = fieldPosition => {
    fields.splice(fieldPosition, 1)
    onChange([...fields])
  }

  const handleSort = ({ oldIndex, newIndex = null }) => {
    const deleted = fields.splice(oldIndex, 1)
    newIndex !== null && fields.splice(newIndex, 0, ...deleted)
    onChange([...fields])
  }

  const handleChange = (fieldPosition, fieldKey, fieldValue) => {
    fields[fieldPosition][fieldKey] = `${fieldValue}`.trim()
    onChange(fields)
  }

  const handleDragEnd = ({ active, over }) => {
    if (!active || !over || active.id === over.id) return
    const oldIndex = fields.findIndex(f => f.id === active.id)
    const newIndex = fields.findIndex(f => f.id === over.id)
    handleSort({ oldIndex, newIndex })
  }

  const columns = [{
    dataField: '',
    text: '',
    className: 'w-8',
    formatter: () => <DragHandleFormatter />,
  }, {
    dataField: 'key',
    text: 'Name',
    formatter: (cell, row, index) => <Input required
      name={`fields.${index}.key`}
      value={row.key}
      onChange={e => handleChange(index, 'key', e.target.value)}
    />,
  }, {
    dataField: 'value',
    text: 'Value',
    formatter: (cell, row, index) => <AutocompleteInput required
      name={`fields.${index}.value`}
      value={row.value}
      maxOptions={0}
      options={{ "{": mergetags }}
      onChange={value => handleChange(index, 'value', value)}
    />,
  }, {
    dataField: 'id',
    text: '',
    className: 'w-8',
    formatter: (cell, row, index) => <ButtonIcon content='Delete' onClick={() => handleRemove(index)}>
      <Trash size={16} />
    </ButtonIcon>,
  }]

  return <>
    <Button className='float-right' size="sm" color="accent" onClick={handleAdd}>
      <Plus size={14} />
      Add
    </Button>
    <FormSection>
      Fields
    </FormSection>
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
      <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
        <DefaultTable
          columns={columns}
          data={fields}
          keyField='id'
          rowWrapper={SortableRow}
          className='max-h-[40vh] overflow-y-auto'
        />
      </SortableContext>
    </DndContext>
  </>
}

Items.propTypes = propTypes
